import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  Trophy, Lightning, CircleNotch, ArrowUp, ArrowDown, ChartBar, Warning,
  Graph, Microphone, Eye, Globe, CheckCircle, Timer
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });
const GRADE_C = { A: 'var(--success)', B: 'var(--accent)', C: 'var(--warning)', D: 'var(--error)', F: 'var(--error)' };
const GRADE_C_BG = { A: 'var(--success-bg)', B: 'var(--accent-light)', C: 'var(--warning-bg)', D: 'var(--error-bg)', F: 'var(--error-bg)' };
const COMP_ICONS = { som: Trophy, entity: Graph, voice: Microphone, aigvr: Eye, surface: Globe };

const MockBadge = () => <span className="px-1.5 py-0.5 text-[9px] font-mono bg-[var(--warning-bg)] text-[var(--warning)] rounded ml-2">Mock</span>;

export default function SearchScorePage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState('');
  const [score, setScore] = useState(null);
  const [board, setBoard] = useState([]);
  const [computing, setComputing] = useState('');

  useEffect(() => {
    Promise.all([clientsAPI.list(), axios.get(`${API}/search-score/all/leaderboard`, auth())])
      .then(([c, l]) => { setClients(c.data); setBoard(l.data); if (c.data.length > 0) setSel(c.data[0].id); });
  }, []);

  const compute = async (cid) => {
    setComputing(cid);
    try {
      const res = await axios.get(`${API}/search-score/${cid}`, auth());
      if (cid === sel) setScore(res.data);
      const l = await axios.get(`${API}/search-score/all/leaderboard`, auth());
      setBoard(l.data);
      toast.success(`Score: ${res.data.composite} (${res.data.grade})`);
    } catch { toast.error('Failed'); }
    finally { setComputing(''); }
  };

  const triggerAgent = (agent) => {
    toast.success(`${agent} agent triggered`);
    const map = { geo: '/seo', entity: '/entity', creative: '/creative-lab', ads: '/ads' };
    if (map[agent]) setTimeout(() => navigate(map[agent]), 1000);
  };

  const bd = score?.breakdown || {};
  const lm = score?.live_metrics || {};
  const sev = { high: 'var(--error)', medium: 'var(--warning)', low: 'var(--accent)' };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="search-score-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">Search Everywhere Score</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Unified Discovery Index — the one number that replaces rankings</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => compute(sel)} disabled={computing === sel} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
              {computing === sel ? <CircleNotch size={16} className="animate-spin mr-2" /> : <ChartBar size={16} className="mr-2" />}
              Compute Score
            </Button>
          </div>
        </div>

        {score ? (
          <>
            {/* ═══ PRIMARY SCORE ═══ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="nx-metric-card rounded-lg text-center py-6" data-testid="composite-score">
                <p className="text-[10px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest mb-3">Search Everywhere Score</p>
                <p className="text-7xl font-black font-mono leading-none" style={{ color: GRADE_C[score.grade], fontFamily: 'Chivo' }}>{score.composite}</p>
                <p className="text-lg text-[var(--text-tertiary)] font-mono mt-1">/ 100</p>
                <span className="inline-block mt-3 px-6 py-1.5 rounded text-xl font-black font-mono" style={{ background: GRADE_C_BG[score.grade], color: GRADE_C[score.grade] }}>Grade {score.grade}</span>
                {score.change !== 0 && (
                  <p className={`mt-2 flex items-center justify-center gap-1 font-mono ${score.change > 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                    {score.change > 0 ? <ArrowUp size={14} weight="bold" /> : <ArrowDown size={14} weight="bold" />}
                    {score.change > 0 ? '+' : ''}{score.change} vs previous
                  </p>
                )}
                <div className="flex items-center justify-center gap-3 mt-3 text-[10px] text-[var(--text-tertiary)] font-mono">
                  <span><Timer size={10} className="inline mr-1" />TTV: {score.ttv_days}d</span>
                  {score.auto_refresh_triggered && <span className="px-2 py-0.5 bg-[var(--error-bg)] text-[var(--error)] rounded">Auto-Refresh Triggered</span>}
                </div>
              </div>

              {/* 5 Component Breakdown */}
              <div className="lg:col-span-2 nx-metric-card rounded-lg" data-testid="component-breakdown">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Component Breakdown</h3>
                <div className="space-y-3">
                  {Object.entries(bd).map(([key, v]) => {
                    const Icon = COMP_ICONS[key] || ChartBar;
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <Icon size={16} className="text-[var(--accent)] flex-shrink-0" weight="duotone" />
                        <span className="text-[10px] text-[var(--text-secondary)] font-mono uppercase w-16">{key}</span>
                        <span className="text-[9px] text-[var(--text-tertiary)] font-mono w-8">{v.weight}%</span>
                        <div className="flex-1 h-3 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{
                            width: `${Math.min(v.score, 100)}%`,
                            background: v.score >= 60 ? 'var(--success)' : v.score >= 30 ? 'var(--warning)' : 'var(--error)',
                          }} />
                        </div>
                        <span className="text-sm font-bold font-mono text-[var(--text-primary)] w-14 text-right">{v.score}%</span>
                        <span className="text-[9px] font-mono text-[var(--text-tertiary)] w-10">={v.weighted}</span>
                        {!v.is_live && <MockBadge />}
                        {v.identity_drift_risk && <span className="px-1.5 py-0.5 text-[8px] bg-[var(--error-bg)] text-[var(--error)] rounded font-mono">Drift Risk</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ═══ PROACTIVE INTELLIGENCE ═══ */}
            {score.intelligence?.length > 0 && (
              <div className="nx-metric-card rounded-lg" data-testid="score-intelligence">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                  <Lightning size={16} className="inline mr-2 text-[var(--warning)]" weight="fill" />Proactive Agent Actions
                </h3>
                <table className="w-full border-collapse">
                  <thead><tr className="border-b border-[var(--border)]">
                    <th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[25%]">Signal</th>
                    <th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[40%]">Intelligence</th>
                    <th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[35%]">Action</th>
                  </tr></thead>
                  <tbody>
                    {score.intelligence.map(row => (
                      <tr key={row.id} className="border-b border-[var(--border-light)]">
                        <td className="p-2"><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: sev[row.severity] }} /><span className="text-xs text-[var(--text-primary)]">{row.indicator}</span></div></td>
                        <td className="p-2 text-xs text-[var(--text-secondary)]">{row.intelligence}</td>
                        <td className="p-2"><p className="text-[10px] text-[var(--text-secondary)] mb-1.5">{row.action}</p>
                          <Button size="sm" onClick={() => triggerAgent(row.agent)} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[10px] h-7 px-3">
                            <Lightning size={10} className="mr-1" /> Trigger Agent
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ═══ 4 LIVE METRICS ═══ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="live-metrics">
              {/* M1: AI Sentiment */}
              {lm.sentiment && (
                <div className="nx-metric-card rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-[var(--text-primary)]">AI Sentiment Analysis</span>
                    {lm.sentiment.auto_queue_correction && <span className="text-[9px] bg-[var(--error-bg)] text-[var(--error)] px-2 py-0.5 rounded font-mono">Auto-Correction Queued</span>}
                  </div>
                  <p className="text-3xl font-black font-mono mb-2" style={{ color: lm.sentiment.status === 'green' ? 'var(--success)' : lm.sentiment.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>{lm.sentiment.score}/100</p>
                  <div className="flex items-center gap-1 h-2.5 rounded-full overflow-hidden mb-2">
                    <div className="h-full bg-[var(--success)]" style={{ width: `${lm.sentiment.positive_pct}%` }} />
                    <div className="h-full bg-[var(--warning)]" style={{ width: `${lm.sentiment.neutral_pct}%` }} />
                    <div className="h-full bg-[var(--error)]" style={{ width: `${lm.sentiment.negative_pct}%` }} />
                  </div>
                  <div className="flex gap-3 text-[9px] font-mono mb-2">
                    <span className="text-[var(--success)]">{lm.sentiment.positive_pct}% Pos</span>
                    <span className="text-[var(--warning)]">{lm.sentiment.neutral_pct}% Neutral</span>
                    <span className="text-[var(--error)]">{lm.sentiment.negative_pct}% Neg</span>
                  </div>
                  <div className="flex gap-2 text-[9px] font-mono text-[var(--text-tertiary)]">
                    <span>Tone: <b className="text-[var(--text-primary)]">{lm.sentiment.tone}</b></span>
                    <span>Accuracy: <b className="text-[var(--text-primary)]">{lm.sentiment.accuracy}</b></span>
                    <span>Status: <b className="text-[var(--text-primary)]">{lm.sentiment.recommendation}</b></span>
                  </div>
                </div>
              )}

              {/* M2: Crawl-to-Referral */}
              {lm.crawl_referral && (
                <div className="nx-metric-card rounded-lg">
                  <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block">Crawl-to-Referral Ratio</span>
                  <div className="space-y-1">
                    {Object.entries(lm.crawl_referral).map(([bot, d]) => (
                      <div key={bot} className={`flex items-center gap-2 p-1 rounded text-[10px] font-mono ${d.alert ? 'bg-[var(--error-bg)]' : ''}`}>
                        <span className="w-20 text-[var(--text-secondary)]">{bot.replace('Bot', '')}</span>
                        <span className="text-[var(--text-primary)]">{d.crawls}</span>
                        <span className="text-[var(--text-tertiary)]">/</span>
                        <span className={d.referrals === 0 ? 'text-[var(--error)]' : 'text-[var(--success)]'}>{d.referrals}</span>
                        <span className="text-[var(--text-tertiary)]">= {d.ratio}%</span>
                        {d.alert && <Warning size={10} className="text-[var(--error)] ml-auto" weight="fill" />}
                      </div>
                    ))}
                  </div>
                  <p className="text-[8px] text-[var(--text-tertiary)] font-mono mt-1 italic">Below 5% = Machine Tax — adjust content gating or robots.txt</p>
                </div>
              )}

              {/* M3: Extraction Efficiency */}
              {lm.extraction_efficiency && (() => {
                const ee = lm.extraction_efficiency;
                return (
                  <div className="nx-metric-card rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold text-[var(--text-primary)]">Extraction Efficiency</span>
                      <span className="text-[9px] text-[var(--text-tertiary)] font-mono">Industry: {ee.benchmark_industry}% | Target: {ee.benchmark_target}%</span>
                    </div>
                    <p className="text-3xl font-black font-mono mb-3" style={{ color: ee.overall >= 40 ? 'var(--success)' : ee.overall >= 25 ? 'var(--warning)' : 'var(--error)' }}>{ee.overall}%</p>
                    {[{ l: 'Tables', v: ee.tables }, { l: 'H3 Lists', v: ee.lists }, { l: 'FAQ Blocks', v: ee.faqs }].map(b => (
                      <div key={b.l} className="flex items-center gap-2 mb-1.5">
                        <span className="text-[10px] text-[var(--text-tertiary)] w-20">{b.l}</span>
                        <div className="flex-1 h-2 bg-[var(--neutral-bg)] rounded-full overflow-hidden relative">
                          <div className="absolute h-full border-r border-dashed border-[var(--border)]" style={{ left: `${ee.benchmark_target}%` }} />
                          <div className="h-full rounded-full" style={{ width: `${b.v}%`, background: b.v >= 40 ? 'var(--success)' : b.v >= 25 ? 'var(--warning)' : 'var(--error)' }} />
                        </div>
                        <span className="text-[10px] font-mono text-[var(--text-primary)] w-10 text-right">{b.v}%</span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* M4: Vernacular Discovery Lift */}
              {lm.vernacular_lift && (() => {
                const vl = lm.vernacular_lift;
                return (
                  <div className="nx-metric-card rounded-lg">
                    <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block">Vernacular Discovery Lift</span>
                    <p className="text-xs text-[var(--text-secondary)] mb-3">{vl.contribution_pct}% of your score improvement this month came from vernacular surfaces</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase mb-1.5">India Split</p>
                        {Object.entries(vl.india || {}).map(([k, v]) => (
                          <div key={k} className="flex items-center gap-1 mb-1">
                            <span className="text-[9px] text-[var(--text-secondary)] w-16 capitalize">{k.replace('_', ' ')}</span>
                            <div className="flex-1 h-1.5 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-[var(--accent)]" style={{ width: `${v}%` }} />
                            </div>
                            <span className="text-[9px] font-mono text-[var(--text-primary)] w-8 text-right">{v}%</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase mb-1.5">GCC Split</p>
                        {Object.entries(vl.gcc || {}).map(([k, v]) => (
                          <div key={k} className="flex items-center gap-1 mb-1">
                            <span className="text-[9px] text-[var(--text-secondary)] w-16 capitalize">{k.replace('_', ' ')}</span>
                            <div className="flex-1 h-1.5 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-[var(--success)]" style={{ width: `${v}%` }} />
                            </div>
                            <span className="text-[9px] font-mono text-[var(--text-primary)] w-8 text-right">{v}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </>
        ) : (
          <div className="nx-metric-card rounded-lg text-center py-16">
            <ChartBar size={48} className="mx-auto mb-4 text-[var(--text-tertiary)]" />
            <p className="text-[var(--text-secondary)]">Click "Compute Score" to calculate the Search Everywhere Score</p>
          </div>
        )}

        {/* ═══ LEADERBOARD ═══ */}
        <div className="nx-metric-card rounded-lg" data-testid="score-leaderboard">
          <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
            <Trophy size={16} className="inline mr-2 text-[var(--warning)]" weight="fill" /> Client Leaderboard
          </h3>
          <table className="nx-table">
            <thead><tr><th>#</th><th>Client</th><th>Score</th><th>Grade</th><th>30D Change</th><th>TTV (days)</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {board.map((c, i) => (
                <tr key={c.client_id}>
                  <td className={`font-mono font-bold ${i < 3 ? 'text-[var(--warning)]' : 'text-[var(--text-tertiary)]'}`}>{i + 1}</td>
                  <td className="!text-[var(--text-primary)] font-medium">{c.client_name}</td>
                  <td className="font-mono font-bold" style={{ color: GRADE_C[c.grade] || 'var(--text-tertiary)' }}>{c.composite != null ? c.composite : '—'}</td>
                  <td>{c.grade ? <span className="font-mono font-bold" style={{ color: GRADE_C[c.grade] }}>{c.grade}</span> : '—'}</td>
                  <td>{c.change != null ? <span className={`flex items-center gap-0.5 font-mono text-sm ${c.change > 0 ? 'text-[var(--success)]' : c.change < 0 ? 'text-[var(--error)]' : 'text-[var(--text-tertiary)]'}`}>{c.change > 0 ? <ArrowUp size={10} /> : c.change < 0 ? <ArrowDown size={10} /> : null}{c.change > 0 ? '+' : ''}{c.change}</span> : '—'}</td>
                  <td className="font-mono">{c.ttv_days || '—'}</td>
                  <td>{c.auto_refresh_triggered ? <span className="px-2 py-0.5 text-[9px] font-mono bg-[var(--error-bg)] text-[var(--error)] rounded">Auto-Triggered</span> : <CheckCircle size={14} className="text-[var(--success)]" weight="fill" />}</td>
                  <td><Button size="sm" variant="ghost" onClick={() => compute(c.client_id)} disabled={computing === c.client_id} className="text-[var(--accent)] p-1">{computing === c.client_id ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} />}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
